import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { MyWorkSections, WaitingOnOthers } from "./work";
import { TodayWidgets } from "./widgets";
import { ago, api, Avatar, can, Empty, Loading, Pill, useLoad, useMe } from "./lib";

const KIND: Record<string, string> = {
  content_review: "Content", work_review: "Development", release_flag: "Release", partner_commitment: "Partner",
  overdue_task: "Overdue", integration_error: "Connection", opportunity: "PR & speaking", github: "GitHub", figma: "Figma", agent_health: "Agents", email: "Email", slack: "Slack", calendar: "Calendar", strategy: "Strategy", other: "Other",
};
const PRIORITY_TONE: Record<string, string> = { urgent: "bad", high: "warn", normal: "", low: "" };
const CONFIDENCE: Record<string, string> = { high: "Confident", medium: "Fairly sure", low: "Unsure" };

function greeting() {
  const h = Number(new Intl.DateTimeFormat("en-US", { hour: "numeric", hour12: false, timeZone: "America/Chicago" }).format(new Date()));
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

export function Today() {
  const { me, toast } = useMe();
  const { data: open, error, reload } = useLoad<any[]>("/api/decisions");
  const { data: done, reload: reloadDone } = useLoad<any[]>("/api/decisions?status=decided");
  const brief = useLoad<any>("/api/briefs/latest").data;
  const ai = useLoad<any>("/api/settings/ai").data;
  const deciders = useLoad<any[]>("/api/deciders").data || [];
  const [busy, setBusy] = useState(false);
  const refresh = () => (reload(), reloadDone());
  const todayYmd = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago" }).format(new Date());
  const doneToday = (done || []).filter((d) => d.decided_at && new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago" }).format(new Date(d.decided_at)) === todayYmd);
  const missingAi = (open || []).filter((d) => !d.ai).length;

  return (
    <div className="page">
      <div className="row" style={{ gap: 16, alignItems: "center" }}>
        <a href="#/settings" title="Your profile" className="hello-photo"><Avatar id={me.id} name={me.name} hasPhoto={me.has_photo} size={56} /></a>
        <div>
        <h2 style={{ margin: 0, font: "600 28px var(--font-display)", letterSpacing: "-0.02em" }}>{greeting()}, {me.name.split(" ")[0]}</h2>
        <p className="muted" style={{ margin: "4px 0 0" }}>
          {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "America/Chicago" })}
          {open ? ` · ${open.length ? `${open.length} thing${open.length === 1 ? "" : "s"} need${open.length === 1 ? "s" : ""} you` : "nothing needs you right now"}` : ""}
        </p>
        </div>
      </div>

      <TodayWidgets open={open ?? null} doneToday={doneToday.length} />

      {brief && <MorningBrief brief={brief} />}

      {ai && !ai.connected && can(me, "decisions.decide") && (
        <div className="banner info">
          <div>
            <strong>Recommendations are off.</strong> Once an Anthropic API key is added, Claude suggests a choice on each decision below, with the reasons. About 2 to 3 cents per recommendation.
          </div>
        </div>
      )}

      <div className="row between" id="decisions" style={{ scrollMarginTop: 80 }}>
        <h2 style={{ margin: 0, font: "600 19px var(--font-display)" }}>Needs your decision</h2>
        {ai?.connected && can(me, "ai.use") && missingAi > 0 && (
          <button
            className="btn"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const r = await api("/api/decisions/recommend-open", { method: "POST" });
                toast(r.errors?.length ? r.errors[0] : `Recommended on ${r.done}${r.remaining ? `; ${r.remaining} left` : ""}`, !!r.errors?.length);
                reload();
              } catch (e: any) {
                toast(e.message, true);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Thinking…" : `Get recommendations (${missingAi})`}
          </button>
        )}
      </div>

      {!open ? (
        <Loading error={error} />
      ) : open.length ? (
        open.map((d) => <DecisionCard key={d.id} d={d} deciders={deciders} aiOn={!!ai?.connected} onDone={refresh} />)
      ) : (
        <Empty>{can(me, "decisions.decide") ? "You're clear. New decisions show up here as they come in: content to approve, dev work to sign off, partner and release questions, and emails or Slack messages the agent flags." : "Nothing for you to decide. When someone hands you a decision, it shows up here."}</Empty>
      )}

      <Suggestions />

      <MyWorkSections skipApprovals={can(me, "decisions.decide")} />

      <WaitingOnOthers />

      {doneToday.length > 0 && (
        <section className="card stack">
          <h2>Decided today</h2>
          {doneToday.map((d) => (
            <div key={d.id} className="item" style={{ alignItems: "flex-start" }}>
              <div className="grow">
                <div className="title">{d.title}</div>
                <div className="small muted">{d.outcome}</div>
                {d.actions.map((a: any) => (
                  <div key={a.id} className="small" style={{ marginTop: 4 }}>
                    <Pill tone={a.status === "drafted" || a.status === "done" ? "good" : a.status === "failed" ? "bad" : "info"}>
                      {a.status === "queued" ? "Waiting for the agent" : a.status === "in_progress" ? "Agent working" : a.status === "drafted" ? "Draft ready" : a.status === "done" ? "Done" : "Agent couldn't finish"}
                    </Pill>{" "}
                    {a.result} {a.result_url && <a href={a.result_url} target="_blank" rel="noreferrer">Open</a>}
                  </div>
                ))}
              </div>
              <span className="muted tiny">{ago(d.decided_at)}</span>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

function DecisionCard({ d, deciders, aiOn, onDone }: { d: any; deciders: any[]; aiOn: boolean; onDone: () => void }) {
  const { me, toast } = useMe();
  const [choice, setChoice] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [rec, setRec] = useState<any>(d.ai);
  const [more, setMore] = useState(false);
  const original: string = d.context.draft ?? "";
  const [draft, setDraft] = useState(original);
  const [editing, setEditing] = useState(false);
  const [talking, setTalking] = useState(false);
  const edited = !!original && draft.trim() !== original.trim();
  const opt = d.options.find((o: any) => o.key === choice);
  const others = deciders.filter((u) => u.id !== me.id);

  const decide = async (key: string) => {
    const o = d.options.find((x: any) => x.key === key);
    if (o?.needs_note && !note.trim()) {
      setChoice(key);
      if (rec?.recommended_option === key && rec.suggested_note) setNote(rec.suggested_note);
      return;
    }
    setBusy(true);
    try {
      // Approving the draft sends your edited version, if you changed it.
      const payload = key === "send_draft" ? { option: key, note: edited ? draft : "" } : { option: key, note };
      if (key === "send_draft" && !draft.trim()) throw new Error("The reply is empty.");
      const r = await api(`/api/decisions/${d.id}/decide`, { method: "POST", body: payload });
      toast(r.outcome || "Done");
      onDone();
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card stack" style={{ gap: 12 }}>
      <div className="row between" style={{ alignItems: "flex-start" }}>
        <div className="stack" style={{ gap: 4, minWidth: 0 }}>
          <div className="row" style={{ gap: 6 }}>
            <Pill>{KIND[d.kind] ?? d.kind}</Pill>
            {d.priority !== "normal" && <Pill tone={PRIORITY_TONE[d.priority]}>{d.priority[0].toUpperCase() + d.priority.slice(1)}</Pill>}
            {d.due_on && <Pill>Due {d.due_on}</Pill>}
            {d.context.from && <span className="muted small">From {d.context.from}</span>}
          </div>
          <strong style={{ fontSize: 16 }}>{d.title}</strong>
          {d.summary && <span className="muted small">{d.summary}</span>}
        </div>
        <span className="muted tiny" style={{ whiteSpace: "nowrap" }}>{ago(d.created_at)}</span>
      </div>

      {d.context.facts?.length > 0 && (
        <div className="stack small" style={{ gap: 4 }}>
          <span className="muted tiny" style={{ fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase" }}>{d.kind === "email" || d.kind === "slack" ? "What they said" : "Details"}</span>
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 3 }}>
            {(more ? d.context.facts : d.context.facts.slice(0, 3)).map((f: string, i: number) => <li key={i} style={{ whiteSpace: "pre-wrap" }}>{f}</li>)}
          </ul>
          {d.context.facts.length > 3 && <button className="btn ghost sm" style={{ padding: 0, justifySelf: "start" }} onClick={() => setMore(!more)}>{more ? "Show less" : `Show all ${d.context.facts.length}`}</button>}
        </div>
      )}
      {original && (
        <div style={{ border: "1px solid var(--line-strong)", borderRadius: 12, padding: 12, background: "var(--panel-2)" }} className="stack">
          <div className="row between">
            <strong className="small">{edited ? "Your edited reply" : "Drafted reply"}</strong>
            <span className="row" style={{ gap: 6 }}>
              {edited && <button className="btn ghost sm" onClick={() => (setDraft(original), setEditing(false))}>Undo my edits</button>}
              {d.kind === "other" && <button className="btn ghost sm" onClick={() => navigator.clipboard.writeText(draft).then(() => toast("Copied. Paste it in Chat."), () => toast("Couldn't copy", true))}>Copy</button>}
              <button className="btn sm" onClick={() => setEditing(!editing)}>{editing ? "Done editing" : "Edit"}</button>
            </span>
          </div>
          {editing ? (
            <textarea className="input" style={{ minHeight: 160, fontSize: 14 }} autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} />
          ) : (
            <div className="small" style={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{draft}</div>
          )}
          <span className="muted tiny">Approving puts this in {d.kind === "slack" ? "Slack" : "Gmail"} as a draft for you to send. Nothing is sent automatically.</span>
        </div>
      )}
      {d.context.links?.length > 0 && (
        <div className="row">{d.context.links.map((l: any) => <a key={l.href} className="pill" href={l.href} target={l.href.startsWith("#") ? undefined : "_blank"} rel="noreferrer">{l.label}</a>)}</div>
      )}

      {rec ? (
        <div style={{ borderRadius: 12, padding: 14 }} className="stack ai-card">
          <div className="row between">
            <span className="small"><span className="ai-spark" /><strong className="ai-mark">Claude suggests</strong> <span className="muted">· {CONFIDENCE[rec.confidence]}</span></span>
            <span className="muted tiny">AI suggestion, check it</span>
          </div>
          <div style={{ fontWeight: 600 }}>{rec.headline}</div>
          <ul className="small" style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 4 }}>{rec.why.map((w: string, i: number) => <li key={i}>{w}</li>)}</ul>
          <div className="small"><span className="muted">For the business: </span>{rec.business_impact}</div>
          {rec.risks?.length > 0 && <div className="small"><span className="muted">Watch for: </span>{rec.risks.join(" · ")}</div>}
          {rec.check_first && <div className="small"><span className="muted">Check first: </span>{rec.check_first}</div>}
        </div>
      ) : aiOn && can(me, "ai.use") ? (
        <div>
          <button
            className="btn sm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const r = await api(`/api/decisions/${d.id}/recommend`, { method: "POST" });
                setRec(r.ai);
              } catch (e: any) {
                toast(e.message, true);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Thinking…" : "Ask Claude"}
          </button>
        </div>
      ) : null}

      {talking && (
        <ItemThread
          id={d.id}
          onDraft={(text) => (setDraft(text), setEditing(false))}
          onDecided={onDone}
        />
      )}

      {opt?.needs_note && (
        <textarea className="input" style={{ minHeight: 70 }} autoFocus value={note} onChange={(e) => setNote(e.target.value)} placeholder={d.kind === "email" || d.kind === "slack" ? "How should the reply go?" : "Add your note"} />
      )}
      <div className="row">
        {d.options.map((o: any) => {
          const suggested = rec?.recommended_option === o.key;
          return (
            <button
              key={o.key}
              className={`btn ${o.tone === "primary" || suggested ? "primary" : ""} ${o.tone === "danger" ? "danger" : ""}`}
              disabled={busy || (choice === o.key && o.needs_note && !note.trim())}
              onClick={() => (choice === o.key ? decide(o.key) : o.needs_note ? decide(o.key) : decide(o.key))}
              title={suggested ? "Claude's suggestion" : undefined}
            >
              {choice === o.key && o.needs_note ? `${o.label}: send` : o.key === "send_draft" && edited ? "Use my edited reply" : o.label}
              {suggested ? " ✓" : ""}
            </button>
          );
        })}
        <span style={{ flex: 1 }} />
        {others.length > 0 && (
          <select
            className="input"
            style={{ width: "auto", fontSize: 13 }}
            value=""
            onChange={async (e) => {
              if (!e.target.value) return;
              await api(`/api/decisions/${d.id}/reassign`, { method: "POST", body: { assignee_id: e.target.value } }).then(() => (toast("Handed off"), onDone())).catch((err) => toast(err.message, true));
            }}
            aria-label="Hand off"
          >
            <option value="">Hand off…</option>
            {others.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
        {aiOn && <button className={`btn sm ${talking ? "primary" : ""}`} onClick={() => setTalking(!talking)}>{talking ? "Hide Claude" : "Talk it through"}</button>}
        <button className="btn ghost sm" disabled={busy} onClick={async () => { await api(`/api/decisions/${d.id}/dismiss`, { method: "POST" }); onDone(); }}>Not now</button>
      </div>
    </section>
  );
}

// Older briefs are one long block of "• Label: text" lines; split them into the same short sections.
// Your private thread with Claude on one item. Claude can offer actions; each is a card that runs only when you press Yes.
function ItemThread({ id, onDraft, onDecided }: { id: string; onDraft: (text: string) => void; onDecided: () => void }) {
  const { toast } = useMe();
  const { data, reload } = useLoad<any>(`/api/decisions/${id}/chat`);
  const [text, setText] = useState("");
  const [sending, setSending] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(t);
    setText("");
    try {
      await api(`/api/decisions/${id}/chat`, { method: "POST", body: { text: t } });
      reload();
    } catch (e: any) {
      setText(t);
      toast(e.message, e.status !== 429);
    } finally {
      setSending(null);
    }
  };
  const yes = async (msg: string, index: number, kind: string) => {
    setActing(`${msg}:${index}`);
    try {
      const r = await api<any>(`/api/decisions/${id}/chat/${msg}/yes`, { method: "POST", body: { index } });
      toast(r.outcome);
      if (kind === "draft" && r.text) onDraft(r.text);
      if (kind === "decide") return onDecided();
      reload();
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setActing(null);
    }
  };
  if (!data) return <span className="muted small">Loading…</span>;
  if (!data.ai) return <span className="muted small">AI isn't connected yet.</span>;
  return (
    <div className="stack" style={{ gap: 10, borderRadius: 12, padding: 14 }}>
      <span className="small"><span className="ai-spark" /><strong className="ai-mark">Working on this with Claude</strong> <span className="muted tiny">· only you see this · AI, check it</span></span>
      {!data.messages.length && !sending && <span className="muted small">Ask anything about this item, or ask for a draft. Claude knows the item, its record and the company context. If it suggests doing something, you'll get a card to say yes.</span>}
      {data.messages.map((m: any) => (
        <div key={m.id} className="stack" style={{ gap: 6, alignItems: m.role === "user" ? "flex-end" : "stretch" }}>
          <div className="small" style={{ whiteSpace: "pre-wrap", lineHeight: 1.55, padding: "8px 12px", borderRadius: 12, maxWidth: "92%", background: m.role === "user" ? "var(--panel-2)" : "transparent", border: m.role === "user" ? "1px solid var(--line)" : "none" }}>{m.text}</div>
          {m.proposals.map((p: any, i: number) => (
            <div key={i} className="row between" style={{ gap: 8, border: "1px solid var(--line-strong)", borderRadius: 10, padding: "8px 12px" }}>
              <div className="small grow" style={{ minWidth: 0 }}>
                <strong>{p.kind === "decide" ? "Decide" : p.kind === "task" ? "Add a task" : "Use this reply"}: </strong>
                {p.kind === "task" ? `${p.title}${p.due_on ? ` · due ${p.due_on}` : ""}` : p.kind === "decide" ? p.label : "Puts Claude's version in the reply box"}
                {p.kind === "decide" && p.note && <div className="muted tiny" style={{ whiteSpace: "pre-wrap" }}>Note: {p.note}</div>}
                {p.done && <div className="tiny" style={{ color: "var(--good, inherit)" }}>{p.outcome}</div>}
              </div>
              {!p.done && <button className="btn primary sm" disabled={!!acting || !data.open} onClick={() => yes(m.id, i, p.kind)}>{acting === `${m.id}:${i}` ? "Working…" : `Yes, ${p.label.charAt(0).toLowerCase()}${p.label.slice(1)}`}</button>}
            </div>
          ))}
        </div>
      ))}
      {sending && (
        <>
          <div className="small" style={{ whiteSpace: "pre-wrap", alignSelf: "flex-end", padding: "8px 12px", borderRadius: 12, maxWidth: "92%", background: "var(--panel-2)", border: "1px solid var(--line)" }}>{sending}</div>
          <span className="muted small">Claude is thinking…</span>
        </>
      )}
      {data.open && (
        <div className="row" style={{ alignItems: "flex-end" }}>
          <textarea
            className="input grow"
            style={{ minHeight: 44 }}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask Claude about this, or ask for a draft"
          />
          <button className="btn primary sm" disabled={!text.trim() || !!sending} onClick={send}>Send</button>
        </div>
      )}
    </div>
  );
}

function toSections(brief: any): { title: string; items: string[] }[] {
  if (brief.sections?.length) return brief.sections;
  const out: { title: string; items: string[] }[] = [];
  for (const raw of String(brief.body).split(/\n+/)) {
    const line = raw.replace(/^\s*[•\-*]\s*/, "").trim();
    if (!line) continue;
    const m = /^([^:]{2,28}):\s*(.+)$/.exec(line);
    const title = m ? m[1]!.trim() : "Notes";
    const rest = m ? m[2]! : line;
    // Split into sentences, but not after abbreviations like "Inc." or "Dr.".
    const items = rest
      .split(/(?<=[.!?])\s+(?=[A-Z0-9"])/)
      .reduce<string[]>((acc, part) => (acc.length && /\b(Inc|Dr|Mr|Ms|Mrs|St|vs|Co|Jr|Sr)\.$/.test(acc[acc.length - 1]!) ? [...acc.slice(0, -1), `${acc[acc.length - 1]} ${part}`] : [...acc, part]), [])
      .map((x) => x.trim().replace(/\.$/, ""))
      .filter(Boolean);
    const existing = out.find((s) => s.title === title);
    if (existing) existing.items.push(...items);
    else out.push({ title, items });
  }
  return out;
}

const SECTION_TONE: Record<string, string> = { "Needs you": "var(--accent)", "Heads-up": "var(--peach)", Today: "var(--lavender)" };

function BriefView({ brief }: { brief: any }) {
  const sections = toSections(brief);
  return (
    <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))" }}>
      {sections.map((s) => (
        <div key={s.title} style={{ borderLeft: `3px solid ${SECTION_TONE[s.title] ?? "var(--line-strong)"}`, paddingLeft: 12 }}>
          <div className="tiny" style={{ fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 }}>{s.title}</div>
          <ul style={{ margin: 0, paddingLeft: 16, display: "grid", gap: 6, fontSize: 14, lineHeight: 1.45 }}>
            {s.items.map((i, n) => <li key={n}>{i}</li>)}
          </ul>
        </div>
      ))}
    </div>
  );
}

// The morning brief, collapsible. Collapsing remembers this brief only, so tomorrow's opens expanded.
function MorningBrief({ brief }: { brief: any }) {
  const key = "momeaze.brief.collapsed";
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(key) === brief.id; } catch { return false; }
  });
  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { next ? localStorage.setItem(key, brief.id) : localStorage.removeItem(key); } catch {}
  };
  const needs = (brief.sections ?? []).find((s: any) => s.title === "Needs you")?.items?.[0];
  return (
    <section className="card stack">
      <button className="brief-head" onClick={toggle} aria-expanded={!collapsed}>
        <span className="row" style={{ gap: 10, minWidth: 0 }}>
          <ChevronDown size={18} aria-hidden style={{ transform: collapsed ? "rotate(-90deg)" : "none", transition: "transform 0.2s var(--ease)", flex: "none", color: "var(--muted)" }} />
          <h2 style={{ margin: 0 }}>Morning brief</h2>
          {collapsed && needs && <span className="muted small" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>· {needs}</span>}
        </span>
        <span className="muted tiny" style={{ flex: "none" }}>{collapsed ? "Show" : `Written by your Chief of Staff agent ${ago(brief.created_at)}`}</span>
      </button>
      {!collapsed && <BriefView brief={brief} />}
    </section>
  );
}

// Claude's suggested tasks from your team's plans, your job description and (if you connected it) your work email.
// Nothing reaches your list until you press Add.
const SOURCE: Record<string, string> = { plan: "From the plan", role: "Part of your role", email: "From your email" };

function Suggestions() {
  const { toast } = useMe();
  const { data, reload } = useLoad<any>("/api/suggestions");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  if (!data?.enabled || !data.ai) return null;
  const act = async (path: string, body?: object, done = "Added to your tasks") => {
    try {
      await api(path, { method: "POST", body });
      toast(done);
      setEditing(null);
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const check = async () => {
    setBusy(true);
    try {
      const r = await api<any>("/api/suggestions/check", { method: "POST" });
      toast(r.added ? `${r.added} new suggestion${r.added === 1 ? "" : "s"}` : "Nothing new right now");
      reload();
    } catch (e: any) {
      // The daily limit isn't a failure: say so calmly and hide the button.
      toast(e.message, e.status !== 429);
      if (e.status === 429) reload();
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card stack ai-card">
      <div className="row between">
        <div>
          <span className="ai-spark" /><strong className="ai-mark">Suggested for you</strong>
          <span className="muted tiny"> · {data.last_run ? `Claude checked ${ago(data.last_run)}` : "Claude checks each weekday morning"} · only you see these</span>
        </div>
        {data.can_check && <button className="btn sm" disabled={busy} onClick={check}>{busy ? "Checking…" : "Check now"}</button>}
      </div>
      {data.error && <div className="banner warn small">{data.error}</div>}
      {data.suggestions.length ? (
        <div className="list">
          {data.suggestions.map((s: any) =>
            editing === s.id ? (
              <form
                key={s.id}
                className="item stack"
                style={{ alignItems: "stretch" }}
                onSubmit={(e) => {
                  e.preventDefault();
                  const fd = new FormData(e.currentTarget);
                  act(`/api/suggestions/${s.id}/add`, { title: fd.get("title"), due_on: fd.get("due_on") || null });
                }}
              >
                <input className="input" name="title" defaultValue={s.title} required />
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <input className="input" type="date" name="due_on" defaultValue={s.due_on ?? ""} style={{ width: "auto" }} />
                  <button className="btn primary sm">Add</button>
                  <button type="button" className="btn ghost sm" onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </form>
            ) : (
              <div key={s.id} className="item" style={{ alignItems: "flex-start" }}>
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="title">{s.title}</div>
                  <div className="small muted">{s.why}</div>
                  <div className="tiny muted" style={{ marginTop: 2 }}>
                    {SOURCE[s.source] ?? "Suggested"}
                    {s.due_on ? ` · by ${s.due_on}` : ""}
                    {s.link && <> · <a href={s.link} target="_blank" rel="noreferrer">Open email</a></>}
                  </div>
                </div>
                <div className="row" style={{ gap: 6, flex: "none", flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <button className="btn primary sm" onClick={() => act(`/api/suggestions/${s.id}/add`)}>Add</button>
                  <button className="btn sm" onClick={() => setEditing(s.id)}>Change</button>
                  <button className="btn ghost sm" onClick={() => act(`/api/suggestions/${s.id}/dismiss`, undefined, "Dismissed")}>Not this</button>
                </div>
              </div>
            ),
          )}
        </div>
      ) : (
        <span className="muted small">Nothing suggested right now. Claude looks at your team's plans and your role each weekday morning.</span>
      )}
      {!data.email.reading && data.email.google_set_up && (
        <span className="muted tiny">Want requests from your inbox caught too? <a href="#/settings">Connect your work email</a> in Settings.</span>
      )}
    </section>
  );
}
